// English source-of-truth strings for the marketing site. Keys are dotted +
// namespaced by section. Claims are kept defensible ("up to", "typically") on
// purpose — see the regulatory/accuracy notes in the PR. Competitor positioning
// stays QUALITATIVE (model, not numbers): never invent competitor fees/rates.
const en: Record<string, string> = {
  'app.name': 'Caratuva',
  'locale.label': 'Language',

  // SEO / social metadata — per route. Written as ad-campaign landing copy:
  // compelling, specific, honest. `meta.ogLocale` is the shared baseline.
  'meta.ogLocale': 'en_US',

  'meta.home.title': 'Caratuva — Get paid from abroad, receive in reais via Pix',
  'meta.home.description':
    'Sell to clients abroad and get paid in Brazilian reais via Pix. Caratuva turns cross-border invoices into shareable payment links — transparent fee, no foreign account, money in your bank. Built for Brazilian sellers, freelancers, and exporters.',
  'meta.home.social':
    'Get paid from anywhere, receive in reais via Pix. One payment link, a transparent fee, money in your Brazilian bank. No foreign account needed.',

  'meta.integrators.title': 'Caratuva for platforms — cross-border receivables API',
  'meta.integrators.description':
    'Embed cross-border receivables into your platform or marketplace. Caratuva gives you payment intents, connected sellers, signed webhooks, and a full sandbox — built API-first, with compliance included.',
  'meta.integrators.social':
    'Cross-border receivables, built API-first. Payment intents, connected sellers, webhooks, and a full sandbox for platforms and marketplaces.',

  'meta.pricing.title': 'Caratuva pricing — one transparent fee, no surprises',
  'meta.pricing.description':
    'Simple, transparent pricing for sellers and platforms alike. A percentage of what you settle, shown upfront, with volume discounts as you scale. Test mode is free.',
  'meta.pricing.social':
    'One transparent fee on what you settle — shown upfront, with volume discounts. Free to test. Pricing for sellers and platforms.',

  'meta.affiliates.title': 'Caratuva affiliates — earn on every referral for 12 months',
  'meta.affiliates.description':
    'Share your link. When someone you refer gets paid with Caratuva, you earn a share of our fee for their first 12 months, paid to your Pix. Up to 20%.',
  'meta.affiliates.social':
    'Refer a business, earn a share of our fee for each customer’s first 12 months — up to 20%, paid to your Pix.',

  'meta.privacy.title': 'Privacy Policy — Caratuva',
  'meta.privacy.description':
    'How Caratuva collects, uses, and protects your data. No tracking pixels, no ad networks.',

  'meta.terms.title': 'Terms of Service — Caratuva',
  'meta.terms.description': 'The terms that govern your use of Caratuva.',

  // Nav
  'nav.pricing': 'Pricing',
  'nav.integrators': 'Integrators',
  'nav.affiliates': 'Affiliates',
  'nav.docs': 'Docs',
  'nav.signin': 'Sign in',
  'nav.getStarted': 'Start now',
  'nav.getPaidNow': 'Get paid now',
  'nav.home': 'Caratuva home',

  // Home — Hero
  'home.hero.eyebrow': 'For sellers, freelancers & exporters in Brazil',
  'home.hero.headline': 'Get paid from anywhere. Receive in reais.',
  'home.hero.subhead':
    'Sell to clients abroad and receive Brazilian reais via Pix. Send a payment link, your client pays by bank transfer, and the money lands in your account — a transparent fee, no foreign account, nothing new to learn.',
  'home.hero.cta.primary': 'Get paid now',
  'home.hero.cta.secondary': 'For platforms & integrators',
  'home.hero.note': 'Free to start. Sign up, verify, and send your first link in minutes.',

  // Home — Problem (vs. traditional international payout platforms; qualitative)
  'home.problem.title': 'Getting paid from abroad shouldn’t cost you a fortune or a fortnight.',
  'home.problem.subtitle':
    'If you sell across borders, the old way of collecting still gets in your way.',
  'home.problem.slow.title': 'Slow to arrive',
  'home.problem.slow.body':
    'International transfers can crawl through intermediary banks for days before anything lands.',
  'home.problem.spread.title': 'Hidden in the spread',
  'home.problem.spread.body':
    'FX markups and fees are often buried in the exchange rate, so you never quite know what you’ll receive.',
  'home.problem.parked.title': 'Parked far from you',
  'home.problem.parked.body':
    'Traditional payout platforms leave your money sitting in a foreign account until you jump through hoops to bring it home.',

  // Home — How it works
  'home.how.title': 'From invoice to reais in four steps',
  'home.how.subtitle': 'No foreign account, no crypto, nothing new for your client to learn.',
  'home.how.step1.title': 'Sign up',
  'home.how.step1.body': 'Create your account in minutes — no sales call, no setup fee.',
  'home.how.step2.title': 'Verify yourself or your business',
  'home.how.step2.body':
    'Confirm your identity (or your company) once. Compliance is built in and handled for you.',
  'home.how.step3.title': 'Send a payment link',
  'home.how.step3.body':
    'Create an invoice and share the link. Your client pays by bank transfer — nothing new on their side.',
  'home.how.step4.title': 'Receive reais via Pix',
  'home.how.step4.body': 'The money settles and lands in your Brazilian bank account via Pix.',

  // Home — Why Caratuva (trust)
  'home.why.title': 'Everything you need to get paid, nothing you don’t.',
  'home.why.subtitle': 'Built for people who sell across borders and want to be paid at home.',
  'home.why.fee.title': 'One transparent fee',
  'home.why.fee.body':
    'You see the fee upfront, per settled invoice — no hidden FX spread, no surprises.',
  'home.why.pix.title': 'Reais via Pix',
  'home.why.pix.body': 'The exact amount lands in your Brazilian bank account through Pix.',
  'home.why.compliance.title': 'Compliance built in',
  'home.why.compliance.body':
    'KYC and KYB run through a licensed, regulated partner — we never custody your money.',
  'home.why.noforeign.title': 'No foreign account',
  'home.why.noforeign.body':
    'Skip opening or managing an account abroad. Your money comes straight home.',

  // Home — Comparison vs. traditional payout platforms (model, not numbers)
  'home.compare.title': 'A different way to get paid from abroad',
  'home.compare.subtitle':
    'Compared with traditional international payout platforms like Payoneer or Deel.',
  'home.compare.them.label': 'Traditional payout platforms',
  'home.compare.us.label': 'With Caratuva',
  'home.compare.row1.them': 'Your money sits in a foreign-currency account until you withdraw it.',
  'home.compare.row1.us': 'You’re paid straight to your Brazilian bank account in reais.',
  'home.compare.row2.them': 'FX markups are often folded into the exchange rate.',
  'home.compare.row2.us': 'A single fee, shown upfront on every settled invoice.',
  'home.compare.row3.them': 'Withdrawals home can take days and add another cut.',
  'home.compare.row3.us': 'Settlement pays out via Pix, with no separate withdrawal step.',
  'home.compare.row4.them': 'You manage a balance in a currency that isn’t yours.',
  'home.compare.row4.us': 'You just send a link and get paid in reais.',
  'home.compare.note':
    'A qualitative comparison of how the models differ. Payoneer and Deel are trademarks of their respective owners; we don’t quote their fees.',

  // Home — Affiliate teaser
  'home.affiliate.badge': 'Affiliate program',
  'home.affiliate.title': 'Know others who get paid from abroad?',
  'home.affiliate.body':
    'Refer them and earn a share of our fee for their first 12 months — up to 20%, paid to your Pix.',
  'home.affiliate.cta': 'See the program',

  // Home — Final CTA
  'home.cta.title': 'Ready to get paid in reais?',
  'home.cta.subtitle': 'Sign up free, verify once, and send your first payment link today.',
  'home.cta.primary': 'Get paid now',
  'home.cta.secondary': 'For platforms & integrators',

  // Footer
  'footer.tagline': 'Get paid from abroad. Receive in reais, via Pix.',
  'footer.product': 'Product',
  'footer.getPaid': 'Get paid',
  'footer.pricing': 'Pricing',
  'footer.integrators': 'Integrators',
  'footer.docs': 'Documentation',
  'footer.dashboard': 'Dashboard',
  'footer.company': 'Company',
  'footer.contact': 'Contact sales',
  'footer.privacy': 'Privacy Policy',
  'footer.terms': 'Terms of Service',
  'footer.rights': '© {year} Caratuva — a product of Pavezi Software LTDA. All rights reserved.',

  // Legal pages
  'legal.effectiveDate': 'Effective date',

  // Integrators page
  'integrators.eyebrow': 'For platforms & integrators',
  'integrators.title': 'Embed cross-border receivables into your product.',
  'integrators.subtitle':
    'Platforms, marketplaces, and software companies use Caratuva to let their sellers get paid across borders — payment intents, connected sellers, webhooks, and a full sandbox, all API-first.',
  'integrators.cta.primary': 'Start in the sandbox',
  'integrators.cta.secondary': 'Read the docs',

  'integrators.caps.title': 'The building blocks you embed',
  'integrators.caps.subtitle':
    'Everything in the dashboard is in the API — wire it into your own flows.',
  'integrators.caps.intents.title': 'Payment intents',
  'integrators.caps.intents.body':
    'Create a payment intent per invoice and collect from buyers by bank transfer.',
  'integrators.caps.sellers.title': 'Connected sellers',
  'integrators.caps.sellers.body':
    'Onboard your sellers as connected accounts and pay each of them out to their own bank.',
  'integrators.caps.webhooks.title': 'Signed webhooks',
  'integrators.caps.webhooks.body':
    'Get a signed webhook for every payment lifecycle event to keep your systems in sync.',
  'integrators.caps.compliance.title': 'Compliance included',
  'integrators.caps.compliance.body':
    'KYB and KYC run through our licensed partner, so verification isn’t your problem to build.',

  'integrators.dev.title': 'Built API-first.',
  'integrators.dev.subtitle':
    'Integrate invoices, payouts, and webhooks directly. Everything you can do in the dashboard, you can do in the API.',
  'integrators.dev.point.keys':
    'Separate pk_test and pk_live keys keep test and production cleanly apart.',
  'integrators.dev.point.webhooks': 'Signed webhooks for every payment lifecycle event.',
  'integrators.dev.point.openapi': 'A full OpenAPI reference with an interactive playground.',
  'integrators.dev.cta': 'Explore the API docs',
  'integrators.dev.snippet.caption': 'Create a payment intent',

  'integrators.modes.title': 'Test the full flow for free. Go live when you’re ready.',
  'integrators.modes.sandbox.title': 'Test mode',
  'integrators.modes.sandbox.badge': 'Self-serve',
  'integrators.modes.sandbox.item1': 'Sign up and start in minutes — no sales call.',
  'integrators.modes.sandbox.item2': 'Test API keys (pk_test) and test-mode invoices.',
  'integrators.modes.sandbox.item3': 'Run the full payment flow end to end on a test network.',
  'integrators.modes.sandbox.cta': 'Start in the sandbox',
  'integrators.modes.production.title': 'Production',
  'integrators.modes.production.badge': 'Talk to us',
  'integrators.modes.production.item1':
    'Production access requires a short conversation with our team.',
  'integrators.modes.production.item2': 'Business verification (KYB) via our licensed partner.',
  'integrators.modes.production.item3': 'Once approved, switch to live and move real money.',
  'integrators.modes.production.cta': 'Talk to sales',

  'integrators.ctaband.title': 'Build cross-border payments into your platform.',
  'integrators.ctaband.subtitle':
    'Start in the sandbox today. Talk to us when you’re ready for production.',
  'integrators.ctaband.primary': 'Start in the sandbox',
  'integrators.ctaband.secondary': 'Talk to sales',

  // Pricing page
  'pricing.eyebrow': 'Pricing',
  'pricing.title': 'Simple, transparent pricing.',
  'pricing.subtitle': 'A percentage of what you move — shown upfront, with discounts as you grow.',

  // Pricing — Get paid (sellers)
  'pricing.sellers.title': 'Get paid',
  'pricing.sellers.note':
    'Sign up, verify once, and send payment links. You see the fee on every settled invoice — no monthly fee, no hidden FX spread.',
  'pricing.sellers.cta': 'Get paid now',
  'pricing.sellers.referralDiscount':
    'Came through a partner’s link? You get 50% off our fee for your first 30 days.',

  'pricing.fee.title': 'Transaction fee',
  'pricing.fee.amount': 'from 2%',
  'pricing.fee.unit': '(200 bps) per settled invoice',
  'pricing.fee.body': 'Volume- and deal-based discounts available as you scale.',
  'pricing.getpaid.title': 'To get started',
  'pricing.getpaid.amount': 'No cost',
  'pricing.getpaid.body': 'No monthly fee and no setup fee — you only pay when you get paid.',
  'pricing.monthly.title': 'Monthly fee',
  'pricing.monthly.amount': 'Talk to sales',
  'pricing.monthly.body': 'Covers platform access, ongoing compliance, and integration support.',
  'pricing.sandbox.title': 'Test mode',
  'pricing.sandbox.amount': 'No cost',
  'pricing.sandbox.body': 'Full end-to-end testing on a test network, no commitment.',

  // Pricing — Platforms & integrators
  'pricing.integrators.eyebrow': 'Platforms & integrators',
  'pricing.integrators.title': 'Pricing that scales with your platform.',
  'pricing.integrators.subtitle':
    'The same transparent fee, with volume-based rates and a monthly plan for platforms embedding Caratuva.',
  'pricing.integrators.cta.primary': 'Start in the sandbox',
  'pricing.integrators.cta.secondary': 'Talk to sales',

  'pricing.tiers.title': 'Volume-based rates',
  'pricing.tiers.col.volume': 'Monthly volume',
  'pricing.tiers.col.fee': 'Transaction fee',
  'pricing.tiers.row1.volume': 'Up to $100k',
  'pricing.tiers.row1.fee': '2.0%',
  'pricing.tiers.row2.volume': '$100k – $1M',
  'pricing.tiers.row2.fee': '1.5%',
  'pricing.tiers.row3.volume': '$1M – $10M',
  'pricing.tiers.row3.fee': '1.0%',
  'pricing.tiers.row4.volume': '$10M+',
  'pricing.tiers.row4.fee': '0.75%',
  'pricing.tiers.note':
    'Rate applies to your total settled volume each month, across all invoices.',
  'pricing.compare.title': 'How it compares',
  'pricing.compare.col.feature': '',
  'pricing.compare.col.caratuva': 'Caratuva',
  'pricing.compare.col.bank': 'Traditional bank / SWIFT',
  'pricing.compare.row.speed': 'Settlement time',
  'pricing.compare.speed.caratuva': 'Minutes',
  'pricing.compare.speed.bank': '2–5 business days',
  'pricing.compare.row.cost': 'All-in cost',
  'pricing.compare.cost.caratuva': 'From ~2%',
  'pricing.compare.cost.bank': '~3–7%',
  'pricing.compare.row.cambio': 'Câmbio bureaucracy',
  'pricing.compare.cambio.caratuva': 'Handled for you',
  'pricing.compare.cambio.bank': 'Manual, per deal',
  'pricing.compare.row.payout': 'Payout',
  'pricing.compare.payout.caratuva': 'PIX in BRL',
  'pricing.compare.payout.bank': 'Wire',
  'pricing.note':
    'Final rate, discounts, and monthly fee are agreed during onboarding based on your volume and corridors.',
  'pricing.cta.title': 'Ready to get started?',
  'pricing.cta.primary': 'Get paid now',
  'pricing.cta.secondary': 'Start in the sandbox',

  // Affiliates page
  'affiliates.eyebrow': 'Affiliate program',
  'affiliates.title': 'Earn a share of every customer you refer for their first 12 months.',
  'affiliates.subtitle':
    'Share your link. When someone you refer gets paid with Caratuva, you earn a share of our fee for each referred customer’s first 12 months, paid out to your PIX.',
  'affiliates.perk.title': 'Your link is worth something',
  'affiliates.perk.body':
    'Everyone who signs up through it gets 50% off our fee for their first month.',
  'affiliates.how.step1.title': 'Share your link',
  'affiliates.how.step1.body':
    'Send your unique referral link to anyone who sells or gets paid across borders.',
  'affiliates.how.step2.title': 'They get paid with Caratuva',
  'affiliates.how.step2.body':
    'Once they sign up and settle payments through Caratuva, you earn on everything they move for their first 12 months.',
  'affiliates.how.step3.title': 'You get paid via PIX',
  'affiliates.how.step3.body':
    'You earn a share of our fee on every payment they make — more monthly referred volume unlocks a higher share.',
  'affiliates.tiers.title': 'Your share grows with referred volume',
  'affiliates.tiers.subtitle': 'A percentage of the fee Caratuva collects — capped at 20%.',
  'affiliates.tiers.col.volume': 'Monthly referred volume',
  'affiliates.tiers.col.share': 'Your share of our fee',
  'affiliates.tiers.row1.volume': 'Up to $100k',
  'affiliates.tiers.row1.share': '5%',
  'affiliates.tiers.row2.volume': '$100k – $1M',
  'affiliates.tiers.row2.share': '10%',
  'affiliates.tiers.row3.volume': '$1M – $10M',
  'affiliates.tiers.row3.share': '15%',
  'affiliates.tiers.row4.volume': '$10M+',
  'affiliates.tiers.row4.share': '20%',
  'affiliates.tiers.note':
    'Share applies to the monthly volume of the customers you referred, combined, across all their invoices. Capped at 20% of our fee.',
  'affiliates.calculator.heading': 'Estimate your monthly earnings',
  'affiliates.calculator.title': 'Earnings calculator',
  'affiliates.calculator.subtitle':
    'Drag the slider or enter an expected monthly referred volume to see what you could earn.',
  'affiliates.calculator.volumeLabel': 'Expected monthly referred volume (USD)',
  'affiliates.calculator.result.fee': 'Caratuva fee',
  'affiliates.calculator.result.share': 'Your share',
  'affiliates.calculator.result.earnings': 'Your earnings',
  'affiliates.calculator.result.perMonth': 'per month',
  'affiliates.calculator.disclaimer':
    'Estimate only — actual rates may vary and are confirmed when you become an affiliate.',
  'affiliates.cta.title': 'Ready to start earning?',
  'affiliates.cta.subtitle': 'Sign in to Caratuva to get your referral link.',
  'affiliates.cta.primary': 'Get your referral link',

  // Sales-contact modal
  'sales.title': 'Talk to sales',
  'sales.subtitle': 'Tell us about your company and we’ll be in touch to get you to production.',
  'sales.form.company': 'Company name',
  'sales.form.name': 'Your name',
  'sales.form.email': 'Work email',
  'sales.form.phone': 'Phone (optional)',
  'sales.form.message': 'What are you looking to do? (optional)',
  'sales.form.submit': 'Send to sales',
  'sales.form.submitting': 'Sending…',
  'sales.form.privacy': 'We’ll only use your details to follow up about Caratuva.',
  'sales.success.title': 'Thanks — we got it',
  'sales.success.body': 'Our team will reach out shortly at the email you provided.',
  'sales.success.close': 'Close',

  // WhatsApp floating button
  'whatsapp.aria': 'Chat with us on WhatsApp',
};
export default en;
